import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, Star } from 'lucide-react';

const Footer = () => {
  const services = [
    { name: 'Immobilien', path: '/leistungen/immobilien' },
    { name: 'Malerarbeiten', path: '/leistungen/malerarbeiten' },
    { name: 'Bodenbeläge', path: '/leistungen/bodenbelaege' },
    { name: 'Fliesen & Naturstein', path: '/leistungen/fliesen-naturstein' },
    { name: 'Trockenbau', path: '/leistungen/trockenbau' },
    { name: 'Türen & Fenster', path: '/leistungen/tueren-fenster' },
  ];

  return (
    <footer className="bg-[#003B7A] text-white mt-auto" data-testid="main-footer">
      <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Company Info with Logo */}
          <div>
            <img 
              src="https://customer-assets.emergentagent.com/job_cazis-bauexperte/artifacts/ivljyg63_890dbcbe-dfaa-42cf-b225-c992d1d7a016.JPG" 
              alt="Marali Immobilien AG & Baufirma"
              className="h-16 mb-4 rounded"
              data-testid="footer-logo"
            />
            <p className="font-body text-sm text-gray-300 leading-relaxed mb-4">
              Ihr Experte für Immobilien, Bau und Renovation in Graubünden. Qualität und Präzision seit Jahren.
            </p>
            <div className="flex items-center gap-1 mb-3" data-testid="footer-reviews">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="w-4 h-4 fill-[#FFD700] text-[#FFD700]" />
              ))}
              <span className="ml-2 text-sm font-semibold text-[#FFD700]">5.0 Google</span>
            </div>
            <a 
              href="mailto:furis@maraliimmobilien.ch" 
              className="flex items-center gap-2 text-sm text-gray-300 hover:text-[#FFD700] transition-colors"
              data-testid="footer-email"
            >
              <Mail className="w-4 h-4" />
              furis@maraliimmobilien.ch
            </a>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="font-heading font-bold text-lg mb-6 text-white">Navigation</h3>
            <ul className="space-y-3">
              <li>
                <Link to="/" className="font-body text-sm text-gray-300 hover:text-[#FFD700] transition-colors" data-testid="footer-nav-home">
                  Home
                </Link>
              </li>
              <li>
                <Link to="/uber-uns" className="font-body text-sm text-gray-300 hover:text-[#FFD700] transition-colors" data-testid="footer-nav-about">
                  Über uns
                </Link>
              </li>
              <li>
                <Link to="/kontakt" className="font-body text-sm text-gray-300 hover:text-[#FFD700] transition-colors" data-testid="footer-nav-contact">
                  Kontakt
                </Link>
              </li>
            </ul>
          </div>

          {/* Services */}
          <div>
            <h3 className="font-heading font-bold text-lg mb-6 text-white">Leistungen</h3>
            <ul className="space-y-3">
              {services.map((service) => (
                <li key={service.path}>
                  <Link 
                    to={service.path} 
                    className="font-body text-sm text-gray-300 hover:text-[#FFD700] transition-colors"
                    data-testid={`footer-service-${service.path}`}
                  >
                    {service.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h3 className="font-heading font-bold text-lg mb-6 text-white">Kontakt</h3>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-[#FFD700] flex-shrink-0 mt-0.5" />
                <span className="font-body text-sm text-gray-300" data-testid="footer-address">
                  Schellenberg 5<br />7408 Cazis<br />Schweiz
                </span>
              </li>
              <li className="flex items-center gap-3">
                <Phone className="w-5 h-5 text-[#FFD700] flex-shrink-0" />
                <a 
                  href="tel:+41792649063" 
                  className="font-body text-sm text-gray-300 hover:text-[#FFD700] transition-colors"
                  data-testid="footer-phone"
                >
                  +41 79 264 90 63
                </a>
              </li>
              <li>
                <p className="font-body text-xs text-gray-400" data-testid="footer-hours">
                  Öffnungszeiten: Bis 17:00 Uhr
                </p>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-gray-700 mt-12 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="font-body text-sm text-gray-400" data-testid="footer-copyright">
              © {new Date().getFullYear()} Marali Immobilien AG. Alle Rechte vorbehalten.
            </p>
            <div className="flex items-center gap-4 text-sm">
              <Link to="/impressum" className="text-gray-400 hover:text-[#FFD700] transition-colors" data-testid="footer-impressum">
                Impressum
              </Link>
              <span className="text-gray-600">|</span>
              <Link to="/agb" className="text-gray-400 hover:text-[#FFD700] transition-colors" data-testid="footer-agb">
                AGB
              </Link>
              <span className="text-gray-600">|</span>
              <span className="text-gray-400" data-testid="footer-credit">
                Erstellt von{' '}
                <a 
                  href="https://alpenpro.agency/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-[#FFD700] hover:text-white transition-colors font-semibold"
                  data-testid="footer-agency-link"
                >
                  Alpen Pro Agency
                </a>
              </span>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;